import { generate3DModelFilename } from '@/utils/file-utils';
import { Message, Parameter } from '@shared/types';
import { ZipWriter, BlobWriter, BlobReader, TextReader } from '@zip.js/zip.js';

interface DownloadOptions {
  content: Blob | string;
  filename: string;
  mimeType?: string;
}

interface GenerateDownloadFilenameOptions {
  currentMessage?: Message | null;
  fallback?: string;
  extension: string;
}

export interface ColoredBlob {
  color: [number, number, number, number]; // RGBA 0-1
  blob: Blob;
  isGhost?: boolean; // True for % modifier - preview only, exclude from export
  excludedFromExport?: boolean; // True for exclude_from_export() - show in preview (toggleable), exclude from export
}

/**
 * Builds a JSON string from a parameter array.
 * Produces a flat { name: value } object — the same format used by the STL
 * export ZIP and understood by `include <file.json>` preprocessing.
 */
export function buildParametersJson(parameters: Parameter[]): string {
  const obj = parameters.reduce(
    (acc, param) => {
      acc[param.name] = param.value;
      return acc;
    },
    {} as Record<string, unknown>,
  );
  return JSON.stringify(obj, null, 2);
}

/**
 * Triggers a browser download of the current parameter values as a JSON file.
 * The filename defaults to "parameters.json".
 */
export function downloadParametersAsJson(
  parameters: Parameter[],
  filename = 'parameters.json',
): void {
  downloadFile({
    content: buildParametersJson(parameters),
    filename,
    mimeType: 'application/json',
  });
}

/**
 * Downloads a file by creating a temporary download link
 */
export function downloadFile({
  content,
  filename,
  mimeType = 'application/octet-stream',
}: DownloadOptions): void {
  let blob: Blob;

  if (typeof content === 'string') {
    blob = new Blob([content], { type: mimeType });
  } else {
    blob = content;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Generates a filename for downloads using the 3D model filename utility
 */
export function generateDownloadFilename({
  currentMessage,
  fallback = 'parametric-model',
  extension,
}: GenerateDownloadFilenameOptions): string {
  const baseName = generate3DModelFilename({
    conversationTitle: undefined,
    assistantMessage: currentMessage || undefined,
    modelName: undefined,
    fallback,
  });

  return `${baseName}.${extension}`;
}

/**
 * Formats a color array as a hex string for filenames
 */
function formatColorForFilename(
  color: [number, number, number, number],
): string {
  const r = Math.round(color[0] * 255)
    .toString(16)
    .padStart(2, '0');
  const g = Math.round(color[1] * 255)
    .toString(16)
    .padStart(2, '0');
  const b = Math.round(color[2] * 255)
    .toString(16)
    .padStart(2, '0');
  return `${r}${g}${b}`;
}

/**
 * Downloads STL file from blob(s)
 * If coloredBlobs is provided and has multiple entries, creates a ZIP with all colored STLs
 * If parameters are provided, creates a ZIP with STL(s) and parameters.json
 * Ghost objects (% modifier) are excluded from export - they are for preview only
 */
export async function downloadSTLFile(
  output: Blob,
  currentMessage?: Message | null,
  coloredBlobs?: ColoredBlob[],
  parameters?: Parameter[],
): Promise<void> {
  const baseName = generateDownloadFilename({
    currentMessage,
    fallback: 'parametric-model',
    extension: '',
  });

  // Filter out ghost objects and excluded objects
  const exportableBlobs = coloredBlobs?.filter(
    (b) => !b.isGhost && !b.excludedFromExport,
  );

  const shouldZip =
    (exportableBlobs && exportableBlobs.length > 1) ||
    (parameters && parameters.length > 0);

  // If we have multiple exportable colored blobs OR parameters to include, create a ZIP
  if (shouldZip) {
    const zipWriter = new ZipWriter(new BlobWriter('application/zip'));

    // Add STLs
    if (exportableBlobs && exportableBlobs.length > 0) {
      for (let i = 0; i < exportableBlobs.length; i++) {
        const { color, blob } = exportableBlobs[i];
        const colorHex = formatColorForFilename(color);
        const filename =
          exportableBlobs.length === 1
            ? `${baseName}.stl`
            : `${baseName}_color_${i + 1}_${colorHex}.stl`;

        await zipWriter.add(filename, new BlobReader(blob));
      }
    } else {
      // Single output blob
      await zipWriter.add(`${baseName}.stl`, new BlobReader(output));
    }

    // Add parameters.json if parameters exist
    if (parameters && parameters.length > 0) {
      const paramsObject = parameters.reduce(
        (acc, param) => {
          acc[param.name] = param.value;
          return acc;
        },
        {} as Record<string, any>,
      );

      const jsonContent = JSON.stringify(paramsObject, null, 2);
      await zipWriter.add('parameters.json', new TextReader(jsonContent));
    }

    // Add README.md
    const readmeContent = `# Parametric Model Export

This ZIP archive contains your exported 3D model files and parameter configuration from Meshpit.

## Contents

- **.stl files**: The 3D model geometry ready for slicing and printing.
- **parameters.json**: A JSON file containing the parameter values used to generate this specific configuration.

## How to Reuse Parameters

You can load the \`parameters.json\` file back into Meshpit to restore your exact configuration:

1. Open the Meshpit application.
2. Go to the **Parameters** panel.
3. Click the **Import** icon (upload button) in the toolbar.
4. Select the \`parameters.json\` file from this archive.

The parameters will update to match the values in the file.
`;
    await zipWriter.add('README.md', new TextReader(readmeContent));

    const zipBlob = await zipWriter.close();
    const zipFilename = `${baseName}_export.zip`;

    downloadFile({
      content: zipBlob,
      filename: zipFilename,
      mimeType: 'application/zip',
    });
  } else if (exportableBlobs && exportableBlobs.length === 1) {
    // Single colored blob - use it instead of the default
    const filename = `${baseName}.stl`;
    downloadFile({
      content: exportableBlobs[0].blob,
      filename,
      mimeType: 'application/octet-stream',
    });
  } else {
    // Default behavior - single STL
    const filename = `${baseName}.stl`;
    downloadFile({
      content: output,
      filename,
      mimeType: 'application/octet-stream',
    });
  }
}

/**
 * Downloads OpenSCAD code as .scad file
 */
export function downloadOpenSCADFile(
  code: string,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'scad',
  });

  downloadFile({
    content: code,
    filename,
    mimeType: 'text/plain',
  });
}
